import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useTranslation } from '../../i18n/useTranslation.js';
import { notificationsApi } from '../../services/notificationsApi.js';
import { formatCurrency, formatDate, timeAgo } from '../../utils/formatters.js';

function BellIcon() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" />
      <path d="M13.7 21a2 2 0 0 1-3.4 0" />
    </svg>
  );
}

const BADGE_CAP = 9;

/**
 * Topbar notification bell: polls the gateway every 30 s, shows an unread
 * badge and a dropdown panel (click-outside / Escape close, like UserMenu).
 * Query errors degrade to a plain bell without badge - never a page error.
 */
export function NotificationBell() {
  const { t, locale } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  const { data, isError } = useQuery({
    queryKey: ['notifications'],
    queryFn: () => notificationsApi.list(),
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
  });

  const items = (!isError && data?.items) || [];
  const unreadCount = isError ? 0 : Number(data?.meta?.unreadCount || 0);
  const badgeLabel = unreadCount > BADGE_CAP ? `${BADGE_CAP}+` : String(unreadCount);

  // onSettled (not onSuccess): even a failed mutation re-syncs with the server
  // on the next refetch instead of leaving stale optimistic state around.
  const markRead = useMutation({
    mutationFn: (id) => notificationsApi.markRead(id),
    onSettled: () => queryClient.invalidateQueries({ queryKey: ['notifications'] }),
  });
  const readAll = useMutation({
    mutationFn: () => notificationsApi.readAll(),
    onSettled: () => queryClient.invalidateQueries({ queryKey: ['notifications'] }),
  });

  // Click-outside + Escape close (same pattern as the AppShell UserMenu).
  useEffect(() => {
    if (!open) {
      return undefined;
    }
    function handlePointer(event) {
      if (rootRef.current && !rootRef.current.contains(event.target)) {
        setOpen(false);
      }
    }
    function handleKey(event) {
      if (event.key === 'Escape') {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handlePointer);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handlePointer);
      document.removeEventListener('keydown', handleKey);
    };
  }, [open]);

  function titleFor(row) {
    const params = { ...(row.params || {}) };
    if (params.totalAmount !== undefined) {
      params.totalAmount = formatCurrency(params.totalAmount);
    }
    if (params.amount !== undefined) {
      params.amount = formatCurrency(params.amount);
    }
    if (params.startDate !== undefined) {
      params.startDate = formatDate(params.startDate);
    }
    if (params.days !== undefined) {
      // Legacy payloads carry NUMERIC strings ("5.00") - render a plain number.
      const days = Number(params.days);
      if (Number.isFinite(days)) {
        params.days = days;
      }
    }
    return t(`notif.${row.type}`, params);
  }

  function handleItemClick(row) {
    if (!row.readAt) {
      markRead.mutate(row.id);
    }
    setOpen(false);
    if (row.link) {
      navigate(row.link);
    }
  }

  const bellLabel = unreadCount > 0 ? `${t('notif.bell')} (${badgeLabel})` : t('notif.bell');

  return (
    <div className="notif" ref={rootRef}>
      <button
        type="button"
        className="theme-toggle notif-bell"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={bellLabel}
        title={t('notif.title')}
        onClick={() => setOpen((value) => !value)}
      >
        <BellIcon />
        {unreadCount > 0 && (
          <span className="notif-badge" aria-hidden="true">{badgeLabel}</span>
        )}
      </button>
      {open && (
        <div className="notif-panel" role="dialog" aria-label={t('notif.title')}>
          <div className="notif-head">
            <span className="notif-head-title">{t('notif.title')}</span>
            <button
              type="button"
              className="notif-readall"
              onClick={() => readAll.mutate()}
              disabled={unreadCount === 0 || readAll.isPending}
            >
              {t('notif.markAll')}
            </button>
          </div>
          {items.length === 0 ? (
            <p className="notif-empty">{t('notif.empty')}</p>
          ) : (
            <ul className="notif-list">
              {items.map((row) => (
                <li key={row.id}>
                  <button
                    type="button"
                    className={`notif-item${row.readAt ? '' : ' unread'}`}
                    onClick={() => handleItemClick(row)}
                  >
                    <span className="notif-dot" aria-hidden="true" />
                    <span className="notif-body">
                      <span className="notif-text">{titleFor(row)}</span>
                      <span className="notif-time">{timeAgo(row.createdAt, locale)}</span>
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
